// 일별 클릭 컬럼 차트 (IMP-7)
//
// 형태 선택: 일 단위로 **구분된 버킷**이라 선(line)이 아니라 컬럼을 쓴다.
// 선은 날짜 사이를 연속값처럼 이어 보이게 해 "8월 3일 12시에 몇 건" 같은 오독을 부른다.
//
// 단일 계열이므로 범례를 두지 않는다(제목이 무엇을 그렸는지 말한다).
// 값은 모든 막대에 붙이지 않고 축 눈금 + 호버 + 표 보기가 나눠 맡는다.
import { useId, useState } from "react";
import { niceCeiling, type DailyPoint } from "../api/stats.ts";

const BAR_MAX_WIDTH = 24; // 막대는 슬롯을 다 채우지 않는다 — 남는 폭은 여백
const BAR_GAP = 2; // 인접 막대를 가르는 것은 테두리가 아니라 표면색 간격

export function DailyClicksChart({
  points,
  title,
}: {
  points: DailyPoint[];
  title: string;
}) {
  const [showTable, setShowTable] = useState(false);
  const [hover, setHover] = useState<number | null>(null);
  const tableId = useId();

  if (points.length === 0) return <p className="hint">표시할 데이터가 없습니다.</p>;

  const max = Math.max(...points.map((p) => p.clicks));
  const ceiling = niceCeiling(max);
  const peakIndex = points.findIndex((p) => p.clicks === max && max > 0);

  const formatDate = (iso: string) => {
    const [, m, d] = iso.split("-");
    return `${Number(m)}/${Number(d)}`;
  };

  return (
    <figure className="chart">
      <figcaption className="chart-title">{title}</figcaption>

      <div className="chart-body">
        {/* 눈금은 직접 라벨링하지 않은 값을 대신 전달한다 */}
        <div className="chart-axis" aria-hidden="true">
          <span>{ceiling.toLocaleString("ko-KR")}</span>
          <span>{Math.round(ceiling / 2).toLocaleString("ko-KR")}</span>
          <span>0</span>
        </div>

        <div className="chart-plot" style={{ gap: `${BAR_GAP}px` }}>
          {/* 그리드는 표면에서 한 단계만 떨어진 실선 — 데이터보다 뒤로 물러나야 한다 */}
          <div className="chart-grid" aria-hidden="true">
            <span /><span /><span />
          </div>

          {points.map((point, index) => {
            const ratio = ceiling === 0 ? 0 : point.clicks / ceiling;
            const isPeak = index === peakIndex;
            const label = `${point.date} · ${point.clicks.toLocaleString("ko-KR")}회`;

            return (
              // 히트 영역은 막대보다 넓다(슬롯 전체) — 1px 막대를 겨냥하게 두지 않는다
              <button
                type="button"
                key={point.date}
                className="chart-slot"
                aria-label={label}
                onMouseEnter={() => setHover(index)}
                onMouseLeave={() => setHover(null)}
                onFocus={() => setHover(index)}
                onBlur={() => setHover(null)}
              >
                <span
                  className={hover === index ? "chart-bar chart-bar--hover" : "chart-bar"}
                  style={{
                    height: `${Math.max(ratio * 100, point.clicks > 0 ? 2 : 0)}%`,
                    maxWidth: `${BAR_MAX_WIDTH}px`,
                  }}
                />
                {/* 최댓값 하나만 직접 라벨 — 모든 막대에 숫자를 붙이면 아무도 읽지 않는다 */}
                {isPeak && <span className="chart-peak">{point.clicks.toLocaleString("ko-KR")}</span>}
                {hover === index && (
                  <span className="chart-tooltip" role="status">
                    <strong>{point.clicks.toLocaleString("ko-KR")}회</strong>
                    <span>{point.date}</span>
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="chart-xaxis" aria-hidden="true">
        <span>{formatDate(points[0]!.date)}</span>
        {points.length > 2 && <span>{formatDate(points[Math.floor(points.length / 2)]!.date)}</span>}
        <span>{formatDate(points[points.length - 1]!.date)}</span>
      </div>

      {/* 호버는 값을 '보조'할 뿐 가두지 않는다 — 표로도 모든 값에 닿을 수 있어야 한다 */}
      <button
        type="button"
        className="btn-link"
        aria-expanded={showTable}
        aria-controls={tableId}
        onClick={() => setShowTable((v) => !v)}
      >
        {showTable ? "표 닫기" : "표로 보기"}
      </button>

      {showTable && (
        <div className="table-scroll" id={tableId}>
          <table className="link-table">
            <caption className="sr-only">{title} 일별 값</caption>
            <thead>
              <tr>
                <th scope="col">날짜</th>
                <th scope="col">클릭</th>
              </tr>
            </thead>
            <tbody>
              {points.map((point) => (
                <tr key={point.date}>
                  <th scope="row">{point.date}</th>
                  <td className="num">{point.clicks.toLocaleString("ko-KR")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </figure>
  );
}
